import { ArrowDownTrayIcon, ChevronDownIcon, DocumentTextIcon, TableCellsIcon } from "@heroicons/react/24/outline";
import { useFileExport } from "@packages/core-client/downloads/useFileExport.ts";
import { usePrint } from "@packages/core-client/print/usePrint.ts";
import { useCurrentLanguage, useI18nextInstance } from "@squide/i18next";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button.tsx";
import { Menu, MenuContent, MenuItem, MenuLabel, MenuSection } from "@/components/ui/menu.tsx";
import { ProgressCircle } from "@/components/ui/progress-circle.tsx";
import { reviewCyclesI18NextInstanceKey } from "../../../i18n/i18next.ts";
import type { CycleExportFormat, CycleExportItem } from "./resolveCycleExports.ts";
import { useCycleExports } from "./useCycleExports.ts";

const FORMAT_ICON: Record<CycleExportFormat, typeof DocumentTextIcon> = { pdf: DocumentTextIcon, csv: TableCellsIcon };

const FORMAT_LABEL: Record<CycleExportFormat, string> = { pdf: "PDF", csv: "CSV" };

interface CycleExportMenuItemProps {
    item: CycleExportItem;
    label: string;
    onAction: (item: CycleExportItem) => Promise<void>;
}

function CycleExportMenuItem({ item, label, onAction }: CycleExportMenuItemProps) {
    const Icon = FORMAT_ICON[item.format];
    const handleAction = useCallback(() => void onAction(item), [item, onAction]);

    return (
        <MenuItem className="flex gap-3" onAction={handleAction} textValue={label}>
            <MenuLabel className="min-w-0 flex-1">{label}</MenuLabel>
            <span className="text-muted-fg flex shrink-0 items-center gap-1.5 text-xs font-medium">
                <Icon className="size-4" aria-hidden="true" />
                {FORMAT_LABEL[item.format]}
            </span>
        </MenuItem>
    );
}

export interface CycleExportMenuProps {
    cycleId: string;
}

export function CycleExportMenu({ cycleId }: CycleExportMenuProps) {
    const i18nextInstance = useI18nextInstance(reviewCyclesI18NextInstanceKey);
    const { t } = useTranslation("CycleExportMenu", { i18n: i18nextInstance });
    const { groups, tab } = useCycleExports(cycleId);
    const { exportFile, isExporting } = useFileExport(useCurrentLanguage());
    const { print, isPreparing } = usePrint();

    const handleAction = useCallback(
        async (item: CycleExportItem) => {
            if (!item.download) {
                await print();

                return;
            }

            if (!(await exportFile(item.download))) {
                toast.error(t("failed"));
            }
        },
        [exportFile, print, t]
    );

    const isBusy = isExporting || isPreparing;

    return (
        <>
            <Menu>
                <Button intent="secondary" isPending={isBusy} className="print:hidden">
                    {isBusy ? <ProgressCircle isIndeterminate aria-hidden="true" /> : <ArrowDownTrayIcon aria-hidden="true" />}
                    <span>{isBusy ? t("preparing") : t("label")}</span>
                    <ChevronDownIcon aria-hidden="true" />
                </Button>
                <MenuContent placement="bottom end" className="w-76 print:hidden">
                    {groups.map(group => (
                        <MenuSection<CycleExportItem> key={group.id} className="[&>header]:tracking-wide [&>header]:uppercase" label={group.id === "report" ? t("group.report", { tab: t(`tab.${tab}`) }) : t("group.allReviews")} items={group.items}>
                            {item => <CycleExportMenuItem item={item} label={t(`item.${item.id}`)} onAction={handleAction} />}
                        </MenuSection>
                    ))}
                </MenuContent>
            </Menu>

            <output className="sr-only">{isBusy && t("liveRegion.preparing")}</output>
        </>
    );
}
